const PRODUCT_SOCIAL_SITE = 'de';
const PRODUCT_SOCIAL_API_BASE_URL = 'https://emes-eu.songmics.com';
const PRODUCT_SOCIAL_API_PATH = '/api/shopify/social/product-medias';

if (!customElements.get('stf-card')) {
  customElements.define(
    'stf-card',
    class ProductSocialCard extends HTMLElement {
      constructor() {
        super();
        this.handleMouseLeave = this.handleMouseInteraction.bind(this, 'leave');
        this.handleMouseEnter = this.handleMouseInteraction.bind(this, 'enter');
      }

      get video() {
        return this.querySelector('video-element, video.stf-card__video:not([hidden])');
      }

      connectedCallback() {
        if (!this.video) return;

        this.addEventListener('mouseleave', this.handleMouseLeave);
        this.addEventListener('mouseenter', this.handleMouseEnter);
      }

      disconnectedCallback() {
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        this.removeEventListener('mouseenter', this.handleMouseEnter);
      }

      handleMouseInteraction(type) {
        if (!this.video) return;

        if (type === 'enter') {
          this.classList.add('is-hovering');
          if (!this.video.hasAttribute('loaded') && typeof this.video.load === 'function') {
            this.video.load();
          }
          if (typeof this.video.play === 'function') {
            const playPromise = this.video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {
                const onCanPlay = () => {
                  this.video.setAttribute('loaded', '');
                  this.video.play().catch(() => {});
                };
                this.video.addEventListener('canplay', onCanPlay, { once: true });
              });
            }
          }
        } else {
          this.classList.remove('is-hovering');
          if (typeof this.video.pause === 'function') this.video.pause();
        }
      }
    }
  );
}

if (!customElements.get('shop-the-feed')) {
  customElements.define(
    'shop-the-feed',
    class ProductSocialMedia extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.domSelectors = {
          sliderWrapper: '.shop-the-feed__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          pagination: '.swiper-pagination',
        };
        this.classNames = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.sliderWrapper = this.querySelector(this.domSelectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items);
        this.laptopItems = parseInt(this.dataset.laptopItems);
        this.tabletItems = parseInt(this.dataset.tabletItems);
        this.paginationType = this.dataset.paginationType || 'bullets';

        this.sliderInstance = false;
        this.hasSocialApi = Boolean(this.dataset.socialApiUrl && this.dataset.productId && this.dataset.socialSite);

        if (this.enableSlider) {
          const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
          mql.onchange = this.init.bind(this);
          this.init();
        }

        if (this.hasSocialApi) {
          this.sliderWrapper.innerHTML = '';
          this.fetchSocialMediaData();
        } else {
          this.bindNativeVideoLoadState();
        }
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.destroySlider();
        } else {
          this.initSlider();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;
        const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--f-column-gap');
        const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;

        this.sliderOptions = {
          slidesPerView: 2,
          spaceBetween: spaceBetween,
          navigation: {
            nextEl: this.section.querySelector(this.domSelectors.nextEl),
            prevEl: this.section.querySelector(this.domSelectors.prevEl),
          },
          pagination: {
            el: this.section.querySelector(this.domSelectors.pagination),
            type: this.paginationType,
          },
          breakpoints: {
            768: {
              slidesPerView: this.tabletItems > 3 ? 3 : this.tabletItems,
            },
            1024: {
              slidesPerView: this.laptopItems,
            },
            1280: {
              slidesPerView: this.items,
            },
          },
          loop: false,
          threshold: 2,
          watchSlidesProgress: true,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
          },
        };

        this.classList.add(this.classNames.swiper);
        this.sliderWrapper.classList.remove(this.classNames.grid);
        this.sliderWrapper.classList.add(this.classNames.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, [FoxTheme.Swiper.Mousewheel]);
        this.sliderInstance.init();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance && this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });

        if (Shopify.designMode && typeof this.sliderInstance === 'object') {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            let { target } = e;
            const index = Number(target.dataset.index);

            this.sliderInstance.slider.slideTo(index);
          });
        }
      }

      destroySlider() {
        this.classList.remove(this.classNames.swiper);
        this.sliderWrapper.classList.remove(this.classNames.swiperWrapper);
        this.sliderWrapper.classList.add(this.classNames.grid);
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      async fetchSocialMediaData() {
        try {
          const productId = this.dataset.productId;
          const apiUrl = `${PRODUCT_SOCIAL_API_BASE_URL}${PRODUCT_SOCIAL_API_PATH}`;

          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'x-api-site': PRODUCT_SOCIAL_SITE,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              site: PRODUCT_SOCIAL_SITE,
              product_id: productId,
            }),
          });

          if (!response.ok) {
            this.hideSection();
            return;
          }

          const responseJson = await response.json();
          const mediaList = this.getSocialMediaList(responseJson);

          if (!Array.isArray(mediaList) || mediaList.length === 0) {
            this.hideSection();
            return;
          }

          this.renderSocialMediaCards(mediaList);
          this.bindNativeVideoLoadState();

          if (typeof this.sliderInstance === 'object') {
            this.sliderInstance.slider.update();
          }
        } catch (error) {
          console.error('[shop-the-feed] social media request failed:', error);
          this.hideSection();
        }
      }

      getSocialMediaList(payload) {
        return Array.isArray(payload?.data) ? payload.data : [];
      }

      normalizeSocialMediaRecord(item) {
        const rawVideo = item?.video || item?.video_url || item?.videoUrl || '';
        const rawImage = item?.image || item?.image_url || item?.imageUrl || '';
        const videoPath = typeof rawVideo === 'string' ? rawVideo.trim() : '';
        const imagePath = typeof rawImage === 'string' ? rawImage.trim() : '';
        const hasVideo = videoPath.length > 0;

        const mediaType = hasVideo ? 'video' : 'image';
        const mediaUrl = hasVideo ? videoPath : imagePath;
        const coverUrl = imagePath;
        const author = item?.nick_name || item?.nickName || '';
        const link = item?.social_url || item?.socialUrl || '';
        const aspectRatio = '9/16';
        const title = item?.title || '';
        const content = item?.content || '';

        return {
          mediaType,
          mediaUrl,
          coverUrl,
          author,
          link,
          aspectRatio,
          title,
          content,
        };
      }

      renderSocialMediaCards(mediaList) {
        const template = this.querySelector('template[data-social-card-template]');
        if (!template) return;

        this.sliderWrapper.innerHTML = '';

        mediaList.forEach((item, index) => {
          const normalized = this.normalizeSocialMediaRecord(item);
          const cardNode = this.createCardFromTemplate(template, normalized, index);
          if (cardNode) this.sliderWrapper.appendChild(cardNode);
        });
      }

      createCardFromTemplate(template, record, index) {
        const fragment = template.content.cloneNode(true);
        const column = fragment.querySelector('.f-column');
        const mediaWrapper = fragment.querySelector('.stf-card__media');
        const videoElement = fragment.querySelector('video.stf-card__video');
        const sourceElement = videoElement ? videoElement.querySelector('source') : null;
        const imageElement = fragment.querySelector('.stf-card__image img');
        const playButton = fragment.querySelector('.stf-card__play-button');
        const nickNameElement = fragment.querySelector('[data-social-nick-name]');
        const titleElement = fragment.querySelector('[data-social-title]');
        const infoElement = fragment.querySelector('.stf-card__info');

        if (!column || !mediaWrapper || !imageElement) return null;

        const mediaType = record.mediaType;
        const mediaUrl = this.getFormattedMediaUrl(record.mediaUrl);
        const coverUrl = this.getFormattedMediaUrl(record.coverUrl, 1000);
        const altText = record.author || record.title || (mediaType === 'video' ? 'social video' : 'social image');
        const nickName = (record.author || '').trim();
        const title = (record.title || '').trim();

        column.dataset.index = String(index);

        if (nickNameElement) {
          nickNameElement.textContent = '@'+nickName;
          nickNameElement.hidden = nickName.length === 0;
        }
        if (titleElement) {
          titleElement.textContent = title;
          titleElement.hidden = title.length === 0;
        }
        if (infoElement) {
          infoElement.hidden = nickName.length === 0 && title.length === 0;
        }

        if (mediaType === 'video' && mediaUrl) {
          if (sourceElement) sourceElement.src = mediaUrl;
          if (videoElement) {
            videoElement.hidden = false;
            videoElement.muted = true;
            videoElement.playsInline = true;
            videoElement.removeAttribute('loaded');
            videoElement.poster = coverUrl || '';
            videoElement.load();
          }
          imageElement.src = coverUrl || mediaUrl;
          imageElement.alt = altText;
          if (playButton) playButton.hidden = false;
        } else {
          imageElement.src = coverUrl || mediaUrl;
          imageElement.alt = altText;
          if (videoElement) videoElement.remove();
          if (playButton) playButton.hidden = true;
        }

        return fragment;
      }

      bindNativeVideoLoadState() {
        const videos = this.querySelectorAll('video.stf-card__video:not([hidden])');
        videos.forEach((video) => {
          if (video.readyState >= 2) {
            video.setAttribute('loaded', '');
            return;
          }

          video.addEventListener(
            'loadedmetadata',
            () => {
              video.setAttribute('loaded', '');
            },
            { once: true }
          );

          video.addEventListener(
            'loadeddata',
            () => {
              video.setAttribute('loaded', '');
            },
            { once: true }
          );

          video.addEventListener(
            'canplay',
            () => {
              video.setAttribute('loaded', '');
            },
            { once: true }
          );
        });
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      getFormattedMediaUrl(path, width) {
        if (!path) return '';
        if (typeof window.formatMediaUrl === 'function') {
          try {
            return window.formatMediaUrl(path, width);
          } catch (error) {
            console.warn('[shop-the-feed] formatMediaUrl fallback:', error);
          }
        }
        return String(path);
      }

      hideSection() {
        if (this.section) {
          this.section.style.display = 'none';
        }
      }
    }
  );
}
